// CampaignBuilder.js
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../AuthProvider";
import { db } from "../firebase";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  addDoc,
  getDocs,
} from "firebase/firestore";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "./components/Sidebar";
import LeadSelector from "./components/LeadSelector";
import PersonaSelector from "./components/PersonaSelector";
import CampaignDetails from "./components/CampaignDetails";
import SubjectLines from "./components/SubjectLines";
import EmailPreview from "./components/EmailPreview";

/**
 * CampaignBuilder Component
 *
 * This component provides a multi-step form for creating and editing campaigns.
 * It integrates with:
 * - LeadSelector: for selecting leads from the same data source as the Leads page
 * - PersonaSelector: for selecting personas from the same data source as the Personas page
 *
 * Campaigns created here will appear in the Campaigns list page.
 * This component can be accessed directly from the sidebar or from the Campaigns list.
 */
const CampaignBuilder = () => {
  const { user } = useContext(AuthContext);
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State for multi-step form
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Campaign data state
  const [campaign, setCampaign] = useState({
    name: "",
    goal: "Book a demo",
    calendlyLink: "",
    daysBetweenFollowUps: 3,
    numberOfFollowUps: 5,
    timezone: "(GMT+01:00) Central European Time - Belgrade, Central...",
    sendingDays: ["Mo", "Tu", "We", "Th", "Fr"],
    workingHours: {
      Monday: { start: "09:00", end: "18:00" },
      Tuesday: { start: "09:00", end: "18:00" },
      Wednesday: { start: "09:00", end: "18:00" },
      Thursday: { start: "09:00", end: "18:00" },
      Friday: { start: "09:00", end: "18:00" },
    },
    emailSettings: {
      includeEmojis: false,
      includeLeadName: true,
      includeCompanyName: true,
      useAllLowercase: true,
      usePlainLanguage: true,
      trackOpenRate: false,
    },
    subjectLine: "make sales better! enhance sales efficiency",
    emailBody: "",
    selectedLeads: [],
    selectedPersona: null,
    status: "draft",
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 30)), // Default 30 days campaign
    createdAt: new Date(),
    updatedAt: new Date(),
  });

  // Available leads and personas
  const [availableLeads, setAvailableLeads] = useState([]);
  const [availablePersonas, setAvailablePersonas] = useState([]);

  // Active tab for lead selection - default to "existing"
  const [activeLeadTab, setActiveLeadTab] = useState("existing");
  const [activePersonaTab, setActivePersonaTab] = useState("select");

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const leadsRef = collection(
          db,
          "userContent",
          user.uid,
          "aiSdr",
          "collections",
          "leads"
        );

        const leadsSnapshot = await getDocs(leadsRef);
        const leadsList = leadsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAvailableLeads(leadsList);

        const personasRef = collection(
          db,
          "userContent",
          user.uid,
          "aiSdr",
          "collections",
          "personas"
        );

        const personasSnapshot = await getDocs(personasRef);
        const personasList = personasSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAvailablePersonas(personasList);

        if (campaignId) {
          const campaignRef = doc(
            db,
            "userContent",
            user.uid,
            "aiSdr",
            "collections",
            "campaigns",
            campaignId
          );
          const campaignSnap = await getDoc(campaignRef);

          if (campaignSnap.exists()) {
            const campaignData = campaignSnap.data();
            setCampaign((prev) => ({
              ...prev,
              ...campaignData,
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, campaignId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCampaign((prev) => ({ ...prev, [name]: value }));
  };

  const handleWorkingHoursChange = (day, type, value) => {
    setCampaign((prev) => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          [type]: value,
        },
      },
    }));
  };

  const handleDaySelection = (day) => {
    setCampaign((prev) => {
      const currentDays = [...prev.sendingDays];
      if (currentDays.includes(day)) {
        return {
          ...prev,
          sendingDays: currentDays.filter((d) => d !== day),
        };
      } else {
        return {
          ...prev,
          sendingDays: [...currentDays, day],
        };
      }
    });
  };

  const handleEmailSettingChange = (setting, value) => {
    setCampaign((prev) => ({
      ...prev,
      emailSettings: {
        ...prev.emailSettings,
        [setting]: value,
      },
    }));
  };

  // This function receives selected leads from the LeadSelector component
  const handleLeadSelection = (leadIds) => {
    setCampaign((prev) => ({
      ...prev,
      selectedLeads: leadIds,
    }));
  };

  // This function receives a selected persona from the PersonaSelector component
  const handlePersonaSelection = (personaId) => {
    const selectedPersona = availablePersonas.find((p) => p.id === personaId);
    setCampaign((prev) => ({
      ...prev,
      selectedPersona,
    }));
  };

  // Helper function to preserve query parameters in navigation
  const getNavPath = (path) => {
    return `${path}${location.search}`;
  };

  // Save the campaign to Firestore
  const handleSaveCampaign = async (status = "draft") => {
    if (!user) return;

    // Validate required fields
    if (!campaign.name) {
      toast.error("Campaign name is required");
      return;
    }

    if (campaign.selectedLeads.length === 0) {
      toast.error("Please select at least one lead");
      return;
    }

    if (campaign.selectedLeads.length > 100) {
      toast.error("Maximum of 100 leads allowed per campaign");
      return;
    }

    if (!campaign.selectedPersona) {
      toast.error("Please select a persona");
      return;
    }

    if (!campaign.startDate || !campaign.endDate) {
      toast.error("Campaign start and end dates are required");
      return;
    }

    if (campaign.startDate >= campaign.endDate) {
      toast.error("End date must be after start date");
      return;
    }

    setLoading(true);
    try {
      const campaignData = {
        ...campaign,
        status,
        updatedAt: new Date(),
      };

      if (campaignId) {
        await setDoc(
          doc(
            db,
            "userContent",
            user.uid,
            "aiSdr",
            "collections",
            "campaigns",
            campaignId
          ),
          campaignData
        );
        toast.success("Campaign updated successfully");
      } else {
        const docRef = await addDoc(
          collection(
            db,
            "userContent",
            user.uid,
            "aiSdr",
            "collections",
            "campaigns"
          ),
          campaignData
        );
        toast.success("Campaign created successfully");
        navigate(getNavPath(`/ai-sdr/campaign-builder/${docRef.id}`));
      }
    } catch (error) {
      console.error("Error saving campaign:", error);
      toast.error("Failed to save campaign. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStepChange = (newStep) => {
    setStep(newStep);
  };

  // Render appropriate component based on current step
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <LeadSelector
            activeTab={activeLeadTab}
            setActiveTab={setActiveLeadTab}
            availableLeads={availableLeads}
            selectedLeads={campaign.selectedLeads}
            onLeadSelect={handleLeadSelection}
            setAvailableLeads={setAvailableLeads}
            campaignId={campaignId}
          />
        );
      case 2:
        // Persona selection step - uses PersonaSelector component
        // This component shares data with the Personas page
        return (
          <PersonaSelector
            activeTab={activePersonaTab}
            setActiveTab={setActivePersonaTab}
            availablePersonas={availablePersonas}
            selectedPersona={campaign.selectedPersona}
            onPersonaSelect={handlePersonaSelection}
            campaignId={campaignId}
          />
        );
      case 3:
        return (
          <CampaignDetails
            campaign={campaign}
            onChange={handleInputChange}
            onWorkingHoursChange={handleWorkingHoursChange}
            onDaySelection={handleDaySelection}
            campaignId={campaignId}
          />
        );
      case 4:
        return (
          <SubjectLines
            campaign={campaign}
            onChange={handleInputChange}
            onEmailSettingChange={handleEmailSettingChange}
            campaignId={campaignId}
          />
        );
      case 5:
        return (
          <EmailPreview
            campaign={campaign}
            selectedPersona={campaign.selectedPersona}
            selectedLead={availableLeads.find(
              (lead) => lead.id === campaign.selectedLeads[0]
            )}
            campaignId={campaignId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex-1">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold mb-6">Campaign Builder</h1>

          {/* Step Navigation */}
          <div className="flex mb-8 border-b">
            <button
              onClick={() => handleStepChange(1)}
              className={`px-4 py-2 font-medium ${
                step === 1
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500"
              }`}
            >
              Choose Leads
            </button>
            <button
              onClick={() => handleStepChange(2)}
              className={`px-4 py-2 font-medium ${
                step === 2
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500"
              }`}
            >
              Select Persona
            </button>
            <button
              onClick={() => handleStepChange(3)}
              className={`px-4 py-2 font-medium ${
                step === 3
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500"
              }`}
            >
              Campaign Details
            </button>
            <button
              onClick={() => handleStepChange(4)}
              className={`px-4 py-2 font-medium ${
                step === 4
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500"
              }`}
            >
              Subject Lines
            </button>
            <button
              onClick={() => handleStepChange(5)}
              className={`px-4 py-2 font-medium ${
                step === 5
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500"
              }`}
            >
              Preview
            </button>
          </div>

          {/* Content based on active step */}
          <div className="bg-white rounded-lg shadow-md p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : (
              renderStep()
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between mt-8">
            <button
              onClick={() => step > 1 && handleStepChange(step - 1)}
              disabled={step === 1}
              className={`px-6 py-2 rounded-lg font-medium ${
                step === 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Previous
            </button>

            <div>
              {/* Save as draft - will appear in Campaigns list with draft status */}
              <button
                onClick={() => handleSaveCampaign("draft")}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 mr-2"
              >
                Save Draft
              </button>

              {step < 5 ? (
                <button
                  onClick={() => handleStepChange(step + 1)}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
                >
                  Next
                </button>
              ) : (
                /* Launch campaign - will appear in Campaigns list with active status */
                <button
                  onClick={() => handleSaveCampaign("active")}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
                >
                  Launch Campaign
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignBuilder;
